import type { Action } from '@ngrx/store';
import { on, createReducer } from '@ngrx/store';
import { initialContactListState } from '../state/contacts.state';
import {
  CheckMultipleRecords,
  ClearAllSelectedContacts,
  ContactSearchParam,
  FetchContactListError,
  FetchContactListRecords,
  FetchContactListSuccess,
  FetchContactSuccess,
  SelectContact,
  UpdateExportInProgress,
  UpdatePage,
} from '../action/contacts.action';
import _uniq from 'lodash-es/uniq';
import type { ContactsListState } from '../../../../../core/interfaces/contact.interface';

const contactListReducer = createReducer(
  initialContactListState,

  on(FetchContactListRecords, (state) => ({
      ...state,
      loading: true,
      loaded: false,
      error: null,
    })),

  on(FetchContactListSuccess, (state, { listName, page, records, totalRecords }) => ({
      ...state,
      contactList: {
        ...state.contactList,
        listName,
        page,
        totalRecords,
        fetchedContacts: records.map((x) => ({
            id: x.id,
            data: x,
          })),
      },
      loading: false,
      loaded: true,
      error: null,
    })),

  on(FetchContactListError, (state, { error }) => ({
      ...state,
      contactList: {
        ...state.contactList,
        totalRecords: initialContactListState.contactList.totalRecords,
        page: initialContactListState.contactList.page,
        fetchedContacts: [],
      },
      loaded: true,
      loading: false,
      error,
    })),

  on(FetchContactSuccess, (state, { contact }) => ({
      ...state,
      contactList: {
        ...state.contactList,
        selectedContact: contact,
      },
    })),

  on(CheckMultipleRecords, (state: ContactsListState, { ids, isChecked }) => {
    if (isChecked) {
      return addIdsToList(state, ids);
    }
      return removeIdFromList(state, ids);
  }),
  on(ClearAllSelectedContacts, (state) => ({
      ...state,
      contactList: {
        ...state.contactList,
        checkedContacts: [],
      },
    })),

  on(SelectContact, (state, { id }) => ({
      ...state,
      contactList: {
        ...state.contactList,
        selectedRecordId: id,
      },
    })),

  on(UpdatePage, (state, { page }) => ({
      ...state,
      contactList: {
        ...state.contactList,
        page,
      },
    })),

  on(ContactSearchParam, (state, { search }) => ({
      ...state,
      contactList: {
        ...state.contactList,
        fetchedContacts: [],
        page: 1,
        filters: {
          search,
        },
      },
      error: state.error,
      loaded: false,
      loading: true,
    })),

  on(UpdateExportInProgress, (state, { inProgress }) => ({
      ...state,
      contactList: {
        ...state.contactList,
        exportInProgress: inProgress,
      },
    })),
);

export function ContactListReducer(state: ContactsListState | undefined, action: Action) {
  return contactListReducer(state, action);
}

function removeIdFromList(state: ContactsListState, ids) {
  const allChecked = [...state.contactList.checkedContacts].filter((x) => !ids.includes(x));

  return {
    ...state,
    contactList: {
      ...state.contactList,
      checkedContacts: allChecked,
    },
  };
}

function addIdsToList(state: ContactsListState, ids: string[]) {
  const allChecked = _uniq([...(state.contactList.checkedContacts || [])].concat(ids));
  return {
    ...state,
    contactList: {
      ...state.contactList,
      checkedContacts: allChecked,
    },
  };
}
